import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { fullName, dueIn, dateTime, fmtDob } from '../lib/data';
import { useWorkflowAction } from '../api/hooks';
import StatusPill from './StatusPill';
import Confidence from './Confidence';
import AgentChip from './AgentChip';
import HITLBar from './HITLBar';
import NetworkConfidence from './NetworkConfidence';
import NetworkCallForm from './NetworkCallForm';
import ArtifactModal from './ArtifactModal';
import { priorAuthPacket } from '../lib/artifacts';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { toast } from '../lib/aiToast';
import {
  Bot, Clock, ExternalLink, MessageSquare, FileText, ChevronDown, AlertTriangle, Flame, Phone,
} from 'lucide-react';

const AGENT_FOR_QUEUE = { referral: 'referral', authorization: 'auth', communication: 'comms', data_sync: 'sync', network_verification: 'network' };
const AGENT_LABEL = { referral: 'Referral', authorization: 'Authorization', communication: 'Communication', data_sync: 'Data Sync', network_verification: 'Network' };
const railColor = (w, due) => (due?.overdue ? '#e11d48' : w.priority === 'high' ? '#f43f5e' : w.priority === 'medium' ? '#f59e0b' : '#cbd5e1');

// One actionable workflow item (referral / auth / comms / data-sync / network
// verification) with the human-in-the-loop controls. Extracted from the old
// Workflows queue page so the same actions live on the appointment screen.
export default function WorkflowItemCard({ w, patient, payer }) {
  const [status, setStatus] = useState(null);
  const [draftVal, setDraftVal] = useState(w.draft);
  const [editing, setEditing] = useState(false);
  const [why, setWhy] = useState(false);
  const [callOpen, setCallOpen] = useState(false);
  const [packetOpen, setPacketOpen] = useState(false);
  const act = useWorkflowAction();

  const st = status || w.status;
  const resolved = st !== 'awaiting_review';
  const due = dueIn(w.dueAt);
  const isComms = w.queue === 'communication' && w.draft;
  const STATUS_ACTION = { approved: 'approve', rejected: 'reject', escalated: 'escalate', sent: 'send' };
  const agentName = AGENT_LABEL[w.queue];

  const resolve = (s, msg) => {
    setStatus(s); // optimistic
    toast.success(msg, { agent: agentName });
    const action = s === 'approved' && w.queue === 'data_sync' ? 'apply' : STATUS_ACTION[s];
    if (action) act.mutateAsync({ id: w.id, action }).catch(() => {});
  };

  return (
    <div className="dh-card relative overflow-hidden">
      <span className="absolute left-0 top-0 bottom-0 w-1" style={{ background: resolved ? '#e2e8f0' : railColor(w, due) }} />
      <div className="p-4 pl-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap text-xs">
              <AgentChip agent={AGENT_FOR_QUEUE[w.queue]} size="xs" />
              {!resolved && w.priority === 'high' && <span className="pill pill-red"><Flame className="h-3 w-3" />High priority</span>}
            </div>
            <div className="text-[15px] font-semibold text-slate-900 mt-1.5 leading-snug">{w.title}</div>
            {patient && <div className="text-xs text-slate-500 mt-0.5">{fullName(patient)} · DOB {fmtDob(patient?.dob)}</div>}
          </div>
          <div className="flex flex-col items-end gap-1.5 shrink-0">
            {!resolved && (
              due.overdue
                ? <span className="pill pill-red"><AlertTriangle className="h-3 w-3" />{due.text}</span>
                : due.soon
                  ? <span className="pill pill-amber"><Clock className="h-3 w-3" />{due.text}</span>
                  : <span className="inline-flex items-center gap-1 text-[11px] text-slate-400"><Clock className="h-3 w-3" />{due.text}</span>
            )}
            <Confidence value={w.confidence} />
          </div>
        </div>

        {!resolved && (
          <div className="mt-3 flex items-start gap-2 text-sm bg-brand-50/60 border border-brand-100 rounded-lg px-3 py-2">
            <Bot className="h-4 w-4 text-brand-600 mt-0.5 shrink-0" />
            <span className="text-slate-800"><span className="font-medium text-brand-700">Recommended:</span> {w.recommendation}</span>
          </div>
        )}

        {!resolved && (
          <button onClick={() => setWhy((v) => !v)} className="mt-2 inline-flex items-center gap-1 text-[11px] text-slate-400 hover:text-slate-600">
            <ChevronDown className={`h-3 w-3 transition-transform ${why ? 'rotate-180' : ''}`} />why the agent routed this
          </button>
        )}
        {why && !resolved && <p className="mt-1.5 text-[13px] text-slate-600 bg-slate-50 border border-slate-100 rounded-lg px-3 py-2">{w.agentSummary}</p>}

        {isComms && !resolved && (
          <div className="mt-3">
            <div className="text-xs text-slate-500 mb-1.5 flex items-center justify-between">
              <span>Drafted message {editing ? '(editing)' : '(review before sending)'}</span>
              <button className="text-brand-600 hover:underline" onClick={() => setEditing((e) => !e)}>{editing ? 'Done' : 'Edit'}</button>
            </div>
            {editing
              ? <Textarea value={draftVal} onChange={(e) => setDraftVal(e.target.value)} rows={5} className="text-sm" />
              : <div className="dh-inset p-3 text-sm text-slate-700 whitespace-pre-wrap">{draftVal}</div>}
          </div>
        )}

        <div className="mt-3.5 flex items-center justify-between gap-3 flex-wrap">
          {resolved ? (
            <div className="flex items-center gap-2 text-sm"><StatusPill status={st} /><span className="text-slate-400 text-xs">resolved · {dateTime(w.dueAt)}</span></div>
          ) : isComms ? (
            <div className="flex flex-wrap items-center gap-2">
              <Link to={`/app/comms/${w.id}`}><Button size="sm" className="bg-brand-600 hover:bg-brand-700 text-white"><MessageSquare className="h-4 w-4 mr-1.5" />Open conversation</Button></Link>
              <Button size="sm" variant="ghost" className="text-slate-500 hover:text-rose-600" onClick={() => resolve('rejected', 'Dismissed')}>Dismiss</Button>
            </div>
          ) : w.queue === 'data_sync' ? (
            <HITLBar status={st} approveLabel="Apply to PM"
              onApprove={() => resolve('approved', 'Applied payer-verified data to Athena')}
              onEscalate={() => resolve('escalated', 'Escalated for manual review')}
              onReject={() => resolve('rejected', 'Kept existing record')} />
          ) : w.queue === 'network_verification' ? (
            <div className="flex flex-wrap items-center gap-3">
              <Button size="sm" className="bg-brand-600 hover:bg-brand-700 text-white" onClick={() => setCallOpen(true)}><Phone className="h-4 w-4 mr-1.5" />Verify by phone</Button>
              <Button size="sm" variant="ghost" className="text-slate-500 hover:text-rose-600" onClick={() => resolve('rejected', 'Skipped network verification')}>Skip</Button>
              {w.netConfidence && <span className="ml-auto"><NetworkConfidence count={w.netConfidence.count} threshold={w.netConfidence.threshold} level={w.netConfidence.level} /></span>}
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              {w.queue === 'authorization' && (
                <Button size="sm" variant="outline" onClick={() => setPacketOpen(true)}><FileText className="h-4 w-4 mr-1.5" />Generate packet</Button>
              )}
              <HITLBar status={st}
                approveLabel={w.queue === 'referral' ? 'Request referral' : 'Start authorization'}
                onApprove={() => resolve('approved', w.queue === 'referral' ? 'Referral request sent' : 'Authorization initiated')}
                onEscalate={() => resolve('escalated', 'Escalated to manager')}
                onReject={() => resolve('rejected', 'Dismissed')} />
            </div>
          )}
        </div>
      </div>

      <NetworkCallForm open={callOpen} item={w} onClose={() => setCallOpen(false)} />
      <ArtifactModal
        open={packetOpen}
        onClose={() => setPacketOpen(false)}
        title="Prior authorization packet"
        agent="auth"
        generate={() => priorAuthPacket(w, patient, payer)}
        submitLabel="Submit to payer"
        onSubmit={() => toast.success('Prior-auth packet submitted', { agent: 'Authorization' })}
      />
    </div>
  );
}
